import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { authHeaders } from "@/lib/auth";
import { PrimaryButton, InputField } from "@/components/fionova/Primitives";

const API = process.env.REACT_APP_BACKEND_URL;
const call = async (path, body) => {
  const r = await fetch(`${API}/api${path}`, { method: body ? "POST" : "GET", headers: { "Content-Type": "application/json", ...authHeaders() }, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Something went wrong.");
  return d;
};

const Row = ({ ok, label, value, testId }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-0" data-testid={testId} data-ok={ok ? "yes" : "no"}>
    <span className="flex shrink-0 items-center gap-2 text-sm text-slate-700">
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-teal-600" /> : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />}{label}
    </span>
    <span className="min-w-0 break-all text-right text-sm font-medium text-slate-900">{value}</span>
  </div>
);

// Admin-only claim: a one-time code is emailed to the configured admin address (backend-enforced).
const Claim = ({ onDone }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const start = () => call("/admin/claim/start", {}).then(() => { setSent(true); setMsg("We emailed a 6-digit code to your account address."); }).catch((e) => setMsg(e.message));
  const verify = () => call("/admin/claim/verify", { code }).then(onDone).catch((e) => setMsg(e.message));
  return (
    <div className="space-y-3" data-testid="admin-claim">
      <p className="text-sm text-slate-600">Confirm administrator access for this account by entering the code we email to it.</p>
      {!sent ? <PrimaryButton data-testid="admin-claim-start" onClick={start}>Email me a verification code</PrimaryButton> : (
        <>
          <InputField data-testid="admin-claim-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" inputMode="numeric" />
          <PrimaryButton data-testid="admin-claim-verify" onClick={verify} disabled={code.trim().length !== 6}>Verify</PrimaryButton>
        </>
      )}
      {msg && <p className="text-sm text-slate-600" data-testid="admin-claim-msg">{msg}</p>}
    </div>
  );
};

// Confirmed action: requires typing the phrase + the admin's password; the server re-checks both.
const Action = ({ title, desc, phrase, path, extra, onDone, testId }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ confirm: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const run = async () => {
    setBusy(true); setMsg("");
    try { const d = await call(path, { ...f, ...extra }); setMsg(d.message || "Done."); setOpen(false); onDone(); }
    catch (e) { setMsg(e.message); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200" data-testid={testId}>
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="mt-1 text-sm text-slate-600">{desc}</p>
      {!open ? <button className="mt-3 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white" data-testid={`${testId}-open`} onClick={() => setOpen(true)}>{title}</button> : (
        <div className="mt-3 space-y-2">
          <InputField data-testid={`${testId}-confirm`} placeholder={`Type ${phrase}`} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
          <InputField data-testid={`${testId}-password`} type="password" placeholder="Your password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          <PrimaryButton data-testid={`${testId}-submit`} onClick={run} disabled={busy || f.confirm.trim().toUpperCase() !== phrase || !f.password}>{busy ? "Working…" : "Confirm"}</PrimaryButton>
        </div>
      )}
      {msg && <p className="mt-2 break-words text-xs text-slate-500" data-testid={`${testId}-msg`}>{msg}</p>}
    </div>
  );
};

const Checks = ({ c }) => {
  const p = c.prices || {};
  const yes = (b) => (b ? "Yes" : "No");
  const eps = c.webhook_endpoints;
  return (
    <div className="rounded-3xl bg-white p-5 ring-1 ring-slate-200" data-testid="golive-checks">
      <Row testId="chk-key" ok={c.stripe_key_mode === "live"} label="Stripe key mode" value={c.stripe_key_mode} />
      <Row testId="chk-mode" ok={c.stripe_mode_setting === "live"} label="STRIPE_MODE setting" value={c.stripe_mode_setting || "—"} />
      <Row testId="chk-webhook-secret" ok={c.webhook_secret_set} label="Webhook signing secret set" value={yes(c.webhook_secret_set)} />
      <Row testId="chk-webhook-endpoints" ok={!!eps?.some((e) => e.url.endsWith("/api/stripe/webhook") && e.status === "enabled")} label="Webhook endpoint"
        value={eps ? eps.map((e) => `${e.url} (${e.status})`).join(", ") || "none found" : `can't read (${c.webhook_endpoints_error})`} />
      <Row testId="chk-last-webhook" ok={!!c.last_webhook?.at} label="Last webhook received" value={c.last_webhook?.at ? `${c.last_webhook.type} · ${c.last_webhook.livemode ? "live" : "test"} · ${c.last_webhook.at.slice(0, 16)}` : "none yet"} />
      <Row testId="chk-merchant" ok={!!c.merchant_name && !/web-fionova/i.test(c.merchant_name)} label="Name customers see" value={c.merchant_name || `can't read (${c.merchant_name_error})`} />
      <Row testId="chk-price-founders" ok={p.founders_9_99?.ok} label="Existing $9.99/month price" value={p.founders_9_99?.ok ? "OK" : p.founders_9_99?.error} />
      <Row testId="chk-price-intro" ok={p.intro_2_99?.ok} label="New $2.99 first-30-days price" value={p.intro_2_99?.ok ? "OK" : p.intro_2_99?.error} />
      <Row testId="chk-price-monthly" ok={p.monthly_16_99?.ok} label="New $16.99/month price" value={p.monthly_16_99?.ok ? "OK" : p.monthly_16_99?.error} />
      <Row testId="chk-offer" ok={c.offer?.active_here} label="New-subscriber offer active here" value={yes(c.offer?.active_here)} />
      <Row testId="chk-partner-email" ok={c.partnership_email_configured} label="Partnership email setting" value={yes(c.partnership_email_configured)} />
      <Row testId="chk-subscribers" ok label="Active/trialing subscribers (unchanged)" value={c.subscribers?.active_or_trialing} />
      <Row testId="chk-consent" ok label="Users opted in to optional emails" value={`${c.email_consent?.opted_in} of ${c.email_consent?.total_users}`} />
    </div>
  );
};

export default function AdminGoLive() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => call("/admin/go-live").then(setC).catch((e) => setErr(e.message));
  useEffect(() => {
    if (loading || user === undefined) return;
    if (!user) navigate("/login?next=admin", { replace: true });
    else if (user.is_admin) load();
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;
  return (
    <div className="mx-auto min-h-screen max-w-2xl px-5 py-10" data-testid="page-admin-golive">
      <button className="flex items-center gap-1.5 text-sm text-slate-500" onClick={() => navigate("/")} data-testid="golive-back"><ArrowLeft className="h-4 w-4" /> FioNova</button>
      <h1 className="mt-6 flex items-center gap-2 text-3xl font-bold tracking-tight text-slate-900"><ShieldCheck className="h-7 w-7 text-teal-600" /> Go-live check</h1>
      {!user.admin_eligible && <p className="mt-4 text-sm text-slate-600" data-testid="golive-denied">This page is only available to FioNova administrators.</p>}
      {user.admin_eligible && !user.is_admin && <div className="mt-6"><Claim onDone={async () => { await refresh(); }} /></div>}
      {user.is_admin && (
        <div className="mt-6 space-y-5">
          {err && <p className="text-sm text-red-600" data-testid="golive-error">{err}</p>}
          {c && <Checks c={c} />}
          <Action testId="act-prices" title="Create the two new prices" phrase="CREATE PRICES" path="/admin/go-live/create-prices" onDone={load}
            desc="Creates only the $2.99 (first 30 days) and $16.99/month prices in this environment's Stripe account. Safe to repeat: existing prices are never duplicated. Existing subscriptions are not touched." />
          <Action testId="act-offer-on" title="Activate the new-subscriber offer" phrase="ACTIVATE OFFER" path="/admin/go-live/offer" extra={{ active: true }} onDone={load}
            desc="New subscribers will see $2.99 for 30 days, then $16.99/month. Existing subscribers keep their price." />
          <Action testId="act-offer-off" title="Deactivate the offer" phrase="DEACTIVATE OFFER" path="/admin/go-live/offer" extra={{ active: false }} onDone={load}
            desc="New subscribers go back to the 7-day trial then $9.99/month." />
          <Action testId="act-consent" title="Pause optional emails without consent" phrase="PAUSE EMAILS" path="/admin/go-live/email-consent-sync" onDone={load}
            desc="Marks everyone without a documented opt-in as unsubscribed from optional emails in Resend and cancels scheduled follow-ups. Essential emails are unaffected." />
          <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
            <p className="font-semibold text-slate-900">Send a test partnership email</p>
            <button className="mt-3 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white" data-testid="act-partner-test"
              onClick={() => call("/admin/go-live/test-partnership-email", {}).then((d) => setMsg(d.ok ? `Sent to the ${d.sent_to_domain} inbox.` : "Send failed.")).catch((e) => setMsg(e.message))}>Send test</button>
            {msg && <p className="mt-2 text-xs text-slate-500" data-testid="act-partner-test-msg">{msg}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
